import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { ChevronRight, Star } from 'lucide-react'
import { useInlineRename } from '../hooks/useInlineRename'
import { FinderItem as FinderItemType } from '../types'
import { formatBytes, formatDate, getItemIcon } from '../utils'

interface FinderItemProps {
    item: FinderItemType
    isSelected: boolean
    isChecked: boolean
    isDragOver?: boolean
    isRenaming?: boolean
    onClick: (e: React.MouseEvent) => void
    onDoubleClick: () => void
    onContextMenu: (e: React.MouseEvent) => void
    onCheckChange: (checked: boolean) => void
    onDragStart: (e: React.DragEvent) => void
    onDragOver: (e: React.DragEvent) => void
    onDragLeave: (e: React.DragEvent) => void
    onDrop: (e: React.DragEvent) => void
    onRename?: (newName: string) => void
    onRenameCancel?: () => void
}

export function FinderGridItem({
    item,
    isSelected,
    isChecked,
    isDragOver,
    isRenaming = false,
    onClick,
    onDoubleClick,
    onContextMenu,
    onCheckChange,
    onDragStart,
    onDragOver,
    onDragLeave,
    onDrop,
    onRename,
    onRenameCancel,
}: Omit<FinderItemProps, 'viewMode'>) {
    const { editName, setEditName, inputRef, handleSubmit, handleKeyDown } = useInlineRename({
        initialName: item.name,
        isRenaming,
        onRename,
        onCancel: onRenameCancel,
    })

    const icon = getItemIcon(item)

    return (
        <div
            draggable={!isRenaming}
            onClick={isRenaming ? undefined : onClick}
            onDoubleClick={isRenaming ? undefined : onDoubleClick}
            onContextMenu={onContextMenu}
            onDragStart={onDragStart}
            onDragOver={item.type === 'folder' ? onDragOver : undefined}
            onDragLeave={item.type === 'folder' ? onDragLeave : undefined}
            onDrop={item.type === 'folder' ? onDrop : undefined}
            className={cn(
                'group relative flex flex-col items-center w-28 p-3 rounded-xl cursor-pointer transition-all duration-150 select-none border shrink-0',
                'hover:border-[#63316B]/30 hover:bg-[#fdfaff]',
                isSelected ? 'border-[#63316B] bg-[#fdfaff]' : 'border-transparent bg-white',
                isDragOver && 'border-dashed border-2 border-[#63316B] bg-[#63316B]/5 scale-[1.02]'
            )}
        >
            <div
                className={cn(
                    'absolute top-2 left-2 transition-all',
                    isChecked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                )}
            >
                <Checkbox
                    checked={isChecked}
                    onCheckedChange={onCheckChange}
                    onClick={e => e.stopPropagation()}
                    className='w-4 h-4 rounded border-2 border-gray-400 bg-white shadow-sm data-[state=checked]:bg-[#63316B] data-[state=checked]:border-[#63316B]'
                />
            </div>

            {item.favorite && <Star className='absolute w-3 h-3 top-2 right-2 text-amber-400 fill-amber-400' />}

            <div className='relative mb-2'>
                {item.thumbnail || item.url ? (
                    <div className='w-12 h-12 overflow-hidden border rounded-lg bg-gray-50 border-gray-100'>
                        <img
                            src={item.thumbnail || item.url}
                            alt={item.name}
                            className='object-cover w-full h-full'
                            draggable={false}
                        />
                        {item.extension && (
                            <span className='absolute top-0.5 left-0.5 bg-white/90 backdrop-blur px-1 py-0.5 rounded text-[7px] font-semibold uppercase border border-gray-100'>
                                {item.extension}
                            </span>
                        )}
                    </div>
                ) : (
                    <div className='flex items-center justify-center w-12 h-12'>
                        <img src={icon} alt='' className='w-12 h-12' draggable={false} />
                    </div>
                )}
            </div>

            {isRenaming ? (
                <input
                    ref={inputRef}
                    type='text'
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onBlur={handleSubmit}
                    onKeyDown={handleKeyDown}
                    onClick={e => e.stopPropagation()}
                    className='w-full text-[11px] text-center bg-white border border-[#63316B] rounded px-1 py-0.5 outline-none'
                />
            ) : (
                <span className='w-full text-[11px] font-medium leading-tight text-center text-gray-700 line-clamp-2'>
                    {item.name}
                </span>
            )}

            <span className='mt-0.5 text-[10px] text-gray-400 capitalize'>
                {item.type === 'folder'
                    ? item.itemCount !== undefined
                        ? `${item.itemCount} item${item.itemCount !== 1 ? 's' : ''}`
                        : 'Folder'
                    : item.dataType || 'Document'}
            </span>
        </div>
    )
}

interface FinderListItemProps extends Omit<FinderItemProps, 'viewMode'> {
    depth?: number
    isExpanded?: boolean
    onToggleExpand?: () => void
    isLoadingChildren?: boolean
    hasChildren?: boolean
}

export function FinderListItem({
    item,
    isSelected,
    isChecked,
    isDragOver,
    isRenaming = false,
    onClick,
    onDoubleClick,
    onContextMenu,
    onCheckChange,
    onDragStart,
    onDragOver,
    onDragLeave,
    onDrop,
    onRename,
    onRenameCancel,
    depth = 0,
    isExpanded = false,
    onToggleExpand,
    isLoadingChildren = false,
    hasChildren = true,
}: FinderListItemProps) {
    const { editName, setEditName, inputRef, handleSubmit, handleKeyDown } = useInlineRename({
        initialName: item.name,
        isRenaming,
        onRename,
        onCancel: onRenameCancel,
    })

    const icon = getItemIcon(item)
    const indentPadding = depth * 20

    return (
        <tr
            draggable={!isRenaming}
            onClick={isRenaming ? undefined : onClick}
            onDoubleClick={isRenaming ? undefined : onDoubleClick}
            onContextMenu={onContextMenu}
            onDragStart={onDragStart}
            onDragOver={item.type === 'folder' ? onDragOver : undefined}
            onDragLeave={item.type === 'folder' ? onDragLeave : undefined}
            onDrop={item.type === 'folder' ? onDrop : undefined}
            className={cn(
                'cursor-pointer transition-colors',
                isSelected ? 'bg-purple-50/50' : 'hover:bg-gray-50',
                isDragOver && 'bg-[#63316B]/10'
            )}
        >
            <td className='px-6 py-3 w-10'>
                <Checkbox
                    checked={isChecked}
                    onCheckedChange={onCheckChange}
                    onClick={e => e.stopPropagation()}
                    className='w-4 h-4 rounded border-2 border-gray-400 bg-white shadow-sm data-[state=checked]:bg-[#63316B] data-[state=checked]:border-[#63316B]'
                />
            </td>
            <td className='px-4 py-3'>
                <div className='flex items-center gap-2' style={{ paddingLeft: indentPadding }}>
                    {item.type === 'folder' ? (
                        <button
                            onClick={e => {
                                e.stopPropagation()
                                onToggleExpand?.()
                            }}
                            className='p-0.5 rounded hover:bg-gray-200 transition-colors flex-shrink-0 w-5 h-5 flex items-center justify-center'
                        >
                            {isLoadingChildren ? (
                                <div className='w-3 h-3 border-2 border-gray-300 border-t-[#63316B] rounded-full animate-spin' />
                            ) : isExpanded && !hasChildren ? (
                                <span className='w-3 h-3 rounded-full border border-gray-300' />
                            ) : (
                                <ChevronRight
                                    className={cn(
                                        'w-4 h-4 text-gray-400 transition-transform duration-200',
                                        isExpanded && 'rotate-90'
                                    )}
                                />
                            )}
                        </button>
                    ) : (
                        <span className='w-5' />
                    )}
                    <img src={icon} alt='' className='w-5 h-5 flex-shrink-0' draggable={false} />
                    {isRenaming ? (
                        <input
                            ref={inputRef}
                            type='text'
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            onBlur={handleSubmit}
                            onKeyDown={handleKeyDown}
                            onClick={e => e.stopPropagation()}
                            className='text-sm bg-white border border-[#63316B] rounded px-2 py-0.5 outline-none min-w-[150px]'
                        />
                    ) : (
                        <span className='text-sm text-gray-700'>{item.name}</span>
                    )}
                    {item.favorite && <Star className='flex-shrink-0 w-3 h-3 text-amber-400 fill-amber-400' />}
                </div>
            </td>
            <td className='hidden sm:table-cell px-4 py-3 text-xs font-medium text-gray-500 uppercase'>
                {item.type === 'folder' ? 'Folder' : item.extension || 'File'}
            </td>
            <td className='px-4 py-3 text-xs font-medium text-gray-500'>
                {item.type === 'folder'
                    ? item.itemCount !== undefined
                        ? `${item.itemCount} item${item.itemCount !== 1 ? 's' : ''}`
                        : '--'
                    : formatBytes(item.size)}
            </td>
            <td className='px-4 py-3 text-xs font-medium text-gray-500 text-right pr-8'>
                {formatDate(item.updatedAt)}
            </td>
        </tr>
    )
}
